/* External */
import {
  memo,
  Children,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import sortedIndex from 'lodash-es/sortedIndex';
import { useLocation } from 'react-router-dom';

/* Ott-common */
import Icon from '@dtvgo/icon';
import InteractiveElement from '@dtvgo/interactive-element';

/* Components */
import CardCarousel from './CardCarousel';

/* Utils */
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';
import { useIsSidebarExpanded } from '../../providers/SidebarStateContext';
import useClickCallback from '../../hooks/useClickCallback';
import useRefSync from '../../hooks/useRefSync';
import { getBEMClassName } from '../../utils/functions';
import { SELECTED_CAROUSEL_CARD_KEY } from '../../utils/constants';

/* Styles */
import './Carousel.scss';

function Carousel({
  title = undefined,
  className = '',
  children: unfilteredChildren,
  focused,
  maxCardHeightRem,
  maxCardWidthRem = undefined,
  setFocus,
  initialTranslate = 0,
  positionCarousel,
  realFocusKey,
  withDots = false,
  hasFocusedChild,
  stealFocusByDefault = false,
  setPositionCarousel,
  centeredPosition = false,
  roundedArrows = false,
  withGradients = false,
  onTranslateChange = undefined,
  isModalShown,
}) {
  const uniqueCardFocusKey = `${realFocusKey}_CARD_`;
  const container = useRef();
  const { pathname } = useLocation();
  const detailPageUrlRegex = useMemo(() => /.*\/detail\/.*/, []);
  const isNotDetailPage = !detailPageUrlRegex.test(pathname);

  const children = useMemo(
    () => Children.toArray(unfilteredChildren).filter((child) => !!child),
    [unfilteredChildren],
  );
  const numberOfChildren = useMemo(() => Children.count(children), [children]);
  const numberOfChildrenRef = useRefSync(numberOfChildren);

  const [translate, setTranslate] = useState(initialTranslate || 0);

  const [cardWidths, setCardWidths] = useState(() => List());

  const isSidebarExpanded = useIsSidebarExpanded();
  const isSidebarExpandedRef = useRefSync(isSidebarExpanded);

  const cardStartPosition = useMemo(() => {
    const accumulatedCardWidths = [];
    accumulatedCardWidths.push(0);
    let accumulator = 0;
    for (let index = 0; index < cardWidths.size; index += 1) {
      const sizeOfThisItem = cardWidths.get(index);
      accumulator += sizeOfThisItem;
      accumulatedCardWidths.push(accumulator);
    }

    return accumulatedCardWidths;
  }, [cardWidths]);
  const cardStartPositionRef = useRefSync(cardStartPosition);

  const normalizePosition = useCallback(
    (position) => Math.min(numberOfChildrenRef.current - 1, Math.max(0, position)),
    [numberOfChildrenRef],
  );

  const { firstVisible, lastVisible } = useMemo(() => ({
    firstVisible: normalizePosition(sortedIndex(cardStartPosition, translate)),
    lastVisible: normalizePosition(
      sortedIndex(
        cardStartPosition,
        translate + (container.current?.clientWidth || 0) + 1 || 0,
      ) - 2,
    ),
  }), [cardStartPosition, normalizePosition, translate]);

  const firstVisibleRef = useRefSync(firstVisible);
  const lastVisibleRef = useRefSync(lastVisible);

  const showArrowLeft = firstVisible > 0;
  const showArrowRight = lastVisible < numberOfChildren - 1;

  const goToPosition = useCallback((position) => {
    const positionToUse = normalizePosition(position);
    setPositionCarousel(positionToUse);
    setTranslate((currentTranslate) => {
      const startPosition = cardStartPositionRef.current[positionToUse];
      const endPosition = cardStartPositionRef.current[positionToUse + 1];
      if ((!startPosition && startPosition !== 0) || (!endPosition && endPosition !== 0)) {
        return currentTranslate;
      }

      const containerWidth = container.current.clientWidth;
      const isFullyVisible = currentTranslate <= startPosition
        && currentTranslate + containerWidth >= endPosition;
      const maxTranslate = Math.max(
        0,
        cardStartPositionRef.current[cardStartPositionRef.current.length - 1] - containerWidth,
      );
      const normalizeTranslate = (value) => Math.min(maxTranslate, Math.max(0, value));

      if (centeredPosition) {
        return normalizeTranslate(
          (startPosition + (endPosition - startPosition) / 2) - containerWidth / 2,
        );
      }

      if (isFullyVisible) return currentTranslate;

      if (endPosition > currentTranslate + containerWidth) {
        return normalizeTranslate(endPosition - containerWidth);
      }

      return normalizeTranslate(startPosition);
    });
  }, [
    cardStartPositionRef,
    centeredPosition,
    normalizePosition,
    setPositionCarousel,
  ]);

  const prevCard = useClickCallback(useCallback(
    () => {
      goToPosition(firstVisibleRef.current - 1);
    },
    [goToPosition, firstVisibleRef],
  ));

  const nextCard = useClickCallback(useCallback(
    () => {
      goToPosition(lastVisibleRef.current + 1);
    },
    [lastVisibleRef, goToPosition],
  ));

  const dots = useMemo(() => (
    withDots
      ? (Array.from({ length: numberOfChildren }))?.map((undef, index) => (
        <div
          /* eslint-disable-next-line react/no-array-index-key
              -- it's okay since it's only presentational */
          key={index}
          className={getBEMClassName('carousel', 'dot', { active: positionCarousel === index })}
        />
      )) || undefined
      : undefined
  ), [withDots, numberOfChildren, positionCarousel]);

  const cards = useMemo(() => Children.map(children, (card, position) => {
    if (!card) {
      return undefined;
    }
    const handleWidthChange = (width) => {
      setCardWidths((currentCardWidths) => currentCardWidths.set(position, width));
    };
    return (
      <CardCarousel
        width={maxCardWidthRem}
        focusKey={`${uniqueCardFocusKey}${position}`}
        onWidthChange={handleWidthChange}
      >
        {card}
      </CardCarousel>
    );
  }), [children, maxCardWidthRem, uniqueCardFocusKey]);

  const cardContentStyle = useMemo(() => ({
    transform: `translateX(-${translate}px)`,
    height: `${maxCardHeightRem}rem`,
    willChange: 'transform',
  }), [translate, maxCardHeightRem]);

  useEffect(() => {
    onTranslateChange?.(translate);
  }, [onTranslateChange, translate]);

  useEffect(() => {
    goToPosition(positionCarousel);
  }, [goToPosition, positionCarousel, cardStartPosition]);

  useEffect(() => {
    const { positionCarouselStored, titleCarouselStored } = JSON.parse(sessionStorage.getItem(
      SELECTED_CAROUSEL_CARD_KEY,
    )) || {};

    if (title === titleCarouselStored && !!positionCarouselStored) {
      goToPosition(positionCarouselStored);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- To only run on mount
  }, []);

  useEffect(() => {
    if (stealFocusByDefault && !positionCarousel) {
      if (!isSidebarExpandedRef.current && !isModalShown) {
        setFocus();
      } else {
        sessionStorage.setItem('focusableItemInMain', `${uniqueCardFocusKey}0`);
      }
    }
  }, [
    isSidebarExpandedRef,
    isModalShown,
    positionCarousel,
    setFocus,
    stealFocusByDefault,
    uniqueCardFocusKey,
  ]);

  useEffect(() => {
    if (isNotDetailPage && (focused || hasFocusedChild)) {
      if (positionCarousel > 0) {
        setFocus(`${uniqueCardFocusKey}${positionCarousel}`);
        sessionStorage.setItem(
          SELECTED_CAROUSEL_CARD_KEY,
          JSON.stringify({
            positionCarouselStored: positionCarousel,
            titleCarouselStored: title,
          }),
        );
      } else {
        setFocus(`${uniqueCardFocusKey}0`);
        sessionStorage.setItem(
          SELECTED_CAROUSEL_CARD_KEY,
          JSON.stringify({
            positionCarouselStored: 0,
            titleCarouselStored: title,
          }),
        );
      }
    }
  }, [
    uniqueCardFocusKey,
    focused,
    setFocus,
    positionCarousel,
    hasFocusedChild,
    title,
    isNotDetailPage,
  ]);

  return (
    <div className={`${getBEMClassName('carousel', undefined, { 'with-gradients': withGradients })} ${className}`}>
      {(title) && (
        <h3 className={getBEMClassName('carousel', 'title')}>
          {title}
        </h3>
      )}
      <div className={getBEMClassName('carousel', 'container')}>
        <div className={getBEMClassName('carousel', 'arrow-left-container', { visible: showArrowLeft })}>
          <InteractiveElement
            className={getBEMClassName('carousel', 'arrow-left', { rounded: roundedArrows })}
            onMouseDown={prevCard}
          >
            <Icon
              name="arrow-forward"
              className={getBEMClassName('carousel', 'arrow-left-icon')}
            />
          </InteractiveElement>
        </div>
        <div className={getBEMClassName('carousel', 'card-content-container')} ref={container}>
          <div className={getBEMClassName('carousel', 'card-content')} style={cardContentStyle}>
            {cards}
          </div>
        </div>
        <div className={getBEMClassName('carousel', 'arrow-right-container', { visible: showArrowRight })}>
          <InteractiveElement
            className={getBEMClassName('carousel', 'arrow-right', { rounded: roundedArrows })}
            onMouseDown={nextCard}
          >
            <Icon
              name="arrow-forward"
              className={getBEMClassName('carousel', 'arrow-right-icon')}
            />
          </InteractiveElement>
        </div>
      </div>
      {withDots && (
        <div className={getBEMClassName('carousel', 'dots')}>
          {dots}
        </div>
      )}
    </div>
  );
}

Carousel.propTypes = {
  maxCardHeightRem: PropTypes.number.isRequired,
  maxCardWidthRem: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  focused: PropTypes.bool.isRequired,
  setFocus: PropTypes.func.isRequired,
  positionCarousel: PropTypes.number.isRequired,
  realFocusKey: PropTypes.string.isRequired,
  hasFocusedChild: PropTypes.bool.isRequired,
  withDots: PropTypes.bool,
  title: PropTypes.string,
  stealFocusByDefault: PropTypes.bool,
  setPositionCarousel: PropTypes.func.isRequired,
  centeredPosition: PropTypes.bool,
  roundedArrows: PropTypes.bool,
  withGradients: PropTypes.bool,
  initialTranslate: PropTypes.number,
  onTranslateChange: PropTypes.func,
  isModalShown: PropTypes.bool.isRequired,
};

export default memo(withCursorVisibilityFilterFocusable({ trackChildren: true })(memo(Carousel)));
